import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError, currency } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Star, MapPin, BadgeCheck, Send, X, Bookmark } from "lucide-react";
import { toast } from "sonner";

export function ExpertProfile() {
  const { slug } = useParams();
  const [expert, setExpert] = useState(null);
  const [showEnq, setShowEnq] = useState(false);
  useEffect(() => { api.get(`/experts/${slug}`).then((r) => setExpert(r.data)).catch(() => setExpert(false)); }, [slug]);
  if (expert === null) return <div className="py-20 text-center text-[#475569]">Loading…</div>;
  if (expert === false) return <div className="py-20 text-center">Expert not found.</div>;
  return (
    <div data-testid="expert-profile-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="tl-card p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row gap-6">
          <img src={expert.photo} alt={expert.name} className="w-28 h-28 rounded-xl object-cover border border-slate-200" />
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-3xl font-display font-bold">{expert.name}</h1>
              {expert.verified && <span className="flex items-center gap-1 text-xs text-[#0891B2]"><BadgeCheck className="w-4 h-4" /> Verified</span>}
            </div>
            <div className="text-[#475569] mt-1">{expert.professional_title}</div>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#475569]">
              <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#0891B2] text-[#0891B2]" />{expert.rating_average?.toFixed(1)} ({expert.review_count} reviews)</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{expert.location}</span>
              <span>• {expert.experience_years} years exp.</span>
            </div>
            <div className="flex gap-3 mt-6">
              <button data-testid="contact-expert-btn" onClick={() => setShowEnq(true)} className="tl-btn-primary"><Send className="w-4 h-4" /> Contact Expert</button>
              <button data-testid="save-expert-btn" onClick={() => api.post("/wishlist/toggle", { kind: "experts", id: expert.id }).then(() => toast.success("Saved"))} className="tl-btn-ghost"><Bookmark className="w-4 h-4" /> Save</button>
            </div>
          </div>
          <div className="md:text-right md:min-w-[180px]">
            <div className="text-xs text-[#475569]">Starting from</div>
            <div className="font-display text-3xl font-bold">{expert.starting_price ? currency(expert.starting_price) : "—"}</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Section title="About"><p className="text-[#475569] leading-relaxed whitespace-pre-line">{expert.about}</p></Section>
          <Section title="Skills"><div className="flex flex-wrap gap-2">{(expert.skills || []).map(s => <span key={s} className="tl-chip">{s}</span>)}</div></Section>
          <Section title="Software"><div className="flex flex-wrap gap-2">{(expert.software || []).map(s => <span key={s} className="tl-chip">{s}</span>)}</div></Section>
          <Section title="Portfolio">
            {(expert.portfolio || []).length === 0 ? <div className="text-sm text-[#475569]">No portfolio pieces yet.</div> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {expert.portfolio.map((p, i) => (
                  <div key={i} className="tl-card overflow-hidden">
                    <div className="aspect-video"><img src={p.image} alt={p.title} className="w-full h-full object-cover" /></div>
                    <div className="p-4"><div className="font-semibold">{p.title}</div></div>
                  </div>
                ))}
              </div>
            )}
          </Section>
          {(expert.e_files || []).length > 0 && (
            <Section title="E-Files by this creator">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {expert.e_files.map(f => (
                  <Link key={f.id} to={`/shop/product/${f.slug}`} className="tl-card p-3">
                    <img src={(f.images || [])[0]} className="aspect-square object-cover rounded mb-2" alt={f.name} />
                    <div className="text-sm font-medium line-clamp-1">{f.name}</div>
                    <div className="text-xs text-[#475569]">{f.is_free ? "FREE" : currency(f.price)}</div>
                  </Link>
                ))}
              </div>
            </Section>
          )}
        </div>
        <div className="space-y-6">
          <Section title="Services"><div className="flex flex-col gap-2">{(expert.services || []).map(s => <div key={s} className="text-sm text-[#475569]">• {s}</div>)}</div></Section>
          <Section title="Reviews">
            {(expert.reviews || []).length === 0 ? <div className="text-sm text-[#475569]">No reviews yet.</div> : (
              <div className="flex flex-col gap-3">
                {expert.reviews.map(r => (
                  <div key={r.id} className="border-b border-slate-200 pb-3 last:border-0">
                    <div className="flex items-center gap-2"><Star className="w-3 h-3 fill-[#0891B2] text-[#0891B2]" /><span className="text-sm">{r.rating}</span>·<span className="text-sm">{r.user_name}</span></div>
                    <div className="text-sm mt-1">{r.title}</div>
                    <div className="text-xs text-[#475569] mt-1">{r.comment}</div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>

      {showEnq && <EnquiryModal target={expert} targetType="expert" onClose={() => setShowEnq(false)} />}
    </div>
  );
}

function Section({ title, children }) {
  return <div className="tl-card p-5"><div className="tl-label mb-3">{title}</div>{children}</div>;
}

export function EnquiryModal({ target, targetType, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ project_title: "", description: "", budget: "", deadline: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) { toast.error("Please login to send enquiry"); nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, target_type: targetType, target_id: target.id });
      toast.success("Enquiry sent — opening thread…");
      onClose();
      if (data?.conversation_id) nav(`/messages/${data.conversation_id}`);
      else nav("/messages");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="enquiry-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#F8FAFC] border border-slate-200 rounded-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-xl font-semibold">Contact {target.name || target.business_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#475569]" /></button>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-3">
          <input data-testid="enq-title" required placeholder="Project title" value={form.project_title} onChange={(e) => setForm({...form, project_title: e.target.value})} className="tl-input" />
          <textarea data-testid="enq-desc" required placeholder="Describe your project…" rows={4} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="tl-input" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Budget (₹)" value={form.budget} onChange={(e) => setForm({...form, budget: e.target.value})} className="tl-input" />
            <input type="date" value={form.deadline} onChange={(e) => setForm({...form, deadline: e.target.value})} className="tl-input" />
          </div>
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="tl-input" />
          <button data-testid="enq-submit" disabled={busy} className="tl-btn-primary justify-center">{busy ? "Sending…" : "Send Enquiry"}</button>
        </form>
      </div>
    </div>
  );
}

export function PartnerProfile() {
  const { slug } = useParams();
  const [partner, setPartner] = useState(null);
  const [showEnq, setShowEnq] = useState(false);
  useEffect(() => { api.get(`/partners/${slug}`).then(r => setPartner(r.data)).catch(() => setPartner(false)); }, [slug]);
  if (partner === null) return <div className="py-20 text-center text-[#475569]">Loading…</div>;
  if (partner === false) return <div className="py-20 text-center">3D Printer Owner not found.</div>;
  return (
    <div data-testid="partner-profile-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="tl-card overflow-hidden mb-6">
        <div className="aspect-[21/9] max-h-72"><img src={partner.photo} alt={partner.business_name} className="w-full h-full object-cover" /></div>
        <div className="p-6">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-display font-bold">{partner.business_name}</h1>
            {partner.verified && <span className="flex items-center gap-1 text-xs text-[#0891B2]"><BadgeCheck className="w-4 h-4" /> Verified</span>}
          </div>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-[#475569]">
            <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{partner.location}</span>
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#0891B2] text-[#0891B2]" />{partner.rating_average?.toFixed(1)} ({partner.review_count})</span>
            <span>Capacity: {partner.capacity}</span>
          </div>
          <div className="flex gap-3 mt-6">
            <button data-testid="request-quote-btn" onClick={() => setShowEnq(true)} className="tl-btn-primary"><Send className="w-4 h-4" /> Request Quote</button>
            <button data-testid="save-partner-btn" onClick={() => api.post("/wishlist/toggle", { kind: "partners", id: partner.id }).then(() => toast.success("Saved"))} className="tl-btn-ghost"><Bookmark className="w-4 h-4" /> Save</button>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <Section title="Overview"><p className="text-[#475569] leading-relaxed">{partner.description}</p></Section>
        <Section title="Technologies"><div className="flex flex-wrap gap-2">{(partner.technologies || []).map(t => <span key={t} className="tl-chip">{t}</span>)}</div></Section>
        <Section title="Materials"><div className="flex flex-wrap gap-2">{(partner.materials || []).map(t => <span key={t} className="tl-chip">{t}</span>)}</div></Section>
        <Section title="Services"><div className="flex flex-wrap gap-2">{(partner.services || []).map(t => <span key={t} className="tl-chip">{t}</span>)}</div></Section>
        <Section title="Machines">
          <div className="flex flex-col gap-3">
            {(partner.machines || []).map((m, i) => (
              <div key={i} className="p-3 rounded border border-slate-200">
                <div className="font-semibold">{m.name}</div>
                <div className="text-xs text-[#475569]">{m.tech} · Build: {m.build_volume}</div>
              </div>
            ))}
          </div>
        </Section>
      </div>
      {showEnq && <EnquiryModal target={partner} targetType="partner" onClose={() => setShowEnq(false)} />}
    </div>
  );
}
